/**
 * Feature Flags System
 *
 * Simplified — only flags that are actively checked in code remain.
 * Dead flags (never referenced outside this file) have been removed.
 *
 * Usage:
 *   import { features } from './lib/features';
 *   if (features.STRIPE_ENABLED) { ... }
 */

// Helper: read env var with both REACT_APP_ (web) and EXPO_PUBLIC_ (mobile) prefixes
const env = (key) => process.env[`REACT_APP_${key}`] ?? process.env[`EXPO_PUBLIC_${key}`] ?? undefined;

export const features = {
  // ─── Payment & Billing ───────────────────────────────────────
  STRIPE_ENABLED: process.env.REACT_APP_STRIPE_ENABLED === 'true',

  // ─── Server-side Generation (mobile: survives app backgrounding) ─
  SERVER_SIDE_GENERATION: process.env.REACT_APP_SERVER_SIDE_GENERATION === 'true' || process.env.EXPO_PUBLIC_SERVER_SIDE_GENERATION === 'true',

  // ─── Community (all default ON for GA) ───────────────────────
  COMMUNITY_FEED: process.env.REACT_APP_COMMUNITY_FEED !== 'false',
  COMMUNITY_SHARING: process.env.REACT_APP_COMMUNITY_SHARING !== 'false',
  COMMUNITY_REVIEWS: process.env.REACT_APP_COMMUNITY_REVIEWS !== 'false',
  COMMUNITY_DISCOVERY: process.env.REACT_APP_COMMUNITY_DISCOVERY !== 'false',
  COMMUNITY_INNER_CIRCLE: process.env.REACT_APP_COMMUNITY_INNER_CIRCLE !== 'false',
  COMMUNITY_RANKINGS: process.env.REACT_APP_COMMUNITY_RANKINGS !== 'false',
  COMMUNITY_COMPATIBILITY: process.env.REACT_APP_COMMUNITY_COMPATIBILITY !== 'false',
  COMMUNITY_LEADERBOARDS: process.env.REACT_APP_COMMUNITY_LEADERBOARDS !== 'false',
  COMMUNITY_ACHIEVEMENTS: process.env.REACT_APP_COMMUNITY_ACHIEVEMENTS !== 'false',
  COMMUNITY_CITY_GUIDES: process.env.REACT_APP_COMMUNITY_CITY_GUIDES !== 'false',
  COMMUNITY_TASTE_PROFILE: process.env.REACT_APP_COMMUNITY_TASTE_PROFILE !== 'false',
  COMMUNITY_PLANNER_INSIGHTS: process.env.REACT_APP_COMMUNITY_PLANNER_INSIGHTS !== 'false',

  // ─── Amadeus Integration (flights, hotels, airports) ─────────
  AMADEUS_FLIGHTS: env('AMADEUS_FLIGHTS') === 'true',
  AMADEUS_HOTELS: env('AMADEUS_HOTELS') === 'true',
  AMADEUS_AIRPORTS: env('AMADEUS_AIRPORTS') === 'true',

  // ─── Collaborative Trips ──────────────────────────────────────
  COLLABORATIVE_TRIPS: env('COLLABORATIVE_TRIPS') !== 'false',

  // ─── Activity Curation (must-do input, lock/pin, feedback) ───
  ACTIVITY_CURATION: process.env.REACT_APP_ACTIVITY_CURATION !== 'false',

  // ─── Mobile itinerary rebuild (Part B — shared createDayTimeline) ─
  // Default ON as of Step 8. Mobile ItineraryScreen now renders via shared
  // createDayTimeline; legacy buildDayTimeline path is deleted.
  // Env var is the escape hatch: set EXPO_PUBLIC_SHARED_TIMELINE_MOBILE=false
  // to force a value of false for an emergency rollback. Without it set, the
  // flag reads true. Per-device override in useUIStore.timelineEngine still
  // functions; since the legacy branch is gone, 'legacy' override falls back
  // to an empty timeline (intentional — any user who flips it will see the
  // empty state and tell us).
  SHARED_TIMELINE_MOBILE: env('SHARED_TIMELINE_MOBILE') !== 'false',

  // ─── Debug/Development ───────────────────────────────────────
  DEBUG_MODE: process.env.REACT_APP_DEBUG_MODE === 'true' || process.env.NODE_ENV === 'development',
};

/**
 * Tier-based display configuration (for UI display only)
 *
 * NOTE: For actual usage limits, use TIER_LIMITS from AuthContext.js
 */
export const tierConfig = {
  explorer: {
    name: 'Explorer',
    description: 'Perfect for casual travelers',
    price: 0,
    priceDisplay: '$0/month',
  },
  planner: {
    name: 'Planner',
    description: 'For serious travel enthusiasts',
    price: 12,
    priceDisplay: '$12/month',
  },
  admin: {
    name: 'Admin',
    description: 'Full access',
    price: 0,
    priceDisplay: 'Free (Admin)',
  },
};

/**
 * Legacy tier feature access map (for UI feature gates only)
 */
const TIER_FEATURE_ACCESS = {
  explorer: {
    pdfExport: false,
    multiCityTrips: true,
    tripSharing: false,
    collaborativeTrips: false,
  },
  planner: {
    pdfExport: true,
    multiCityTrips: true,
    tripSharing: true,
    collaborativeTrips: true,
  },
  admin: {
    pdfExport: true,
    multiCityTrips: true,
    tripSharing: true,
    collaborativeTrips: true,
  },
};

/**
 * Check if a tier has access to a specific UI feature
 */
export const tierHasFeature = (tier, featureName) => {
  const tierFeatures = TIER_FEATURE_ACCESS[tier] || TIER_FEATURE_ACCESS.explorer;
  return tierFeatures[featureName] === true;
};

/**
 * Log feature flag status (for debugging)
 */
const logFeatureFlags = () => {
  if (features.DEBUG_MODE) {
    console.group('🚩 Feature Flags');
    Object.entries(features).forEach(([name, enabled]) => {
      console.log(`${enabled ? '✅' : '❌'} ${name}: ${enabled}`);
    });
    console.groupEnd();
  }
};

if (process.env.NODE_ENV === 'development') {
  logFeatureFlags();
}

// No default export — use named exports only
